// SPDX-License-Identifier: AGPL-3.0-or-later

import * as PrivateChannelCommands from '@app/features/channel/commands/PrivateChannelCommands';
import Channels from '@app/features/channel/state/Channels';
import * as MessageCommands from '@app/features/messaging/commands/MessageCommands';
import Messages from '@app/features/messaging/state/MessagingMessages';
import * as NavigationCommands from '@app/features/navigation/commands/NavigationCommands';
import {
	normalizeVaultCode,
	VAULT_CODE_TEMP,
	VAULT_HIDDEN_INVITE_CONTENT,
	VAULT_TEMP_INVITE_CONTENT,
	VAULT_VERIFIER_PLAINTEXT,
	type VaultCode,
} from '@app/features/vault/constants';
import Vault, {setVaultSessionKey} from '@app/features/vault/state/Vault';
import {
	decryptVaultText,
	deriveVaultKey,
	encryptVaultText,
	isVaultEnvelope,
	isVaultHiddenInvite,
	isVaultTempInvite,
} from '@app/features/vault/VaultCrypto';
import {ME} from '@fluxer/constants/src/AppConstants';
import {MessageFlags} from '@fluxer/constants/src/ChannelConstants';
import * as SnowflakeUtils from '@fluxer/snowflake/src/SnowflakeUtils';

export function matchVaultCode(channelId: string, content: string): VaultCode | null {
	const channel = Channels.getChannel(channelId);
	if (!channel?.isPersonalNotes()) return null;
	return normalizeVaultCode(content);
}

export function codeToSet(code: VaultCode): 'hidden' | 'temp' {
	return code === VAULT_CODE_TEMP ? 'temp' : 'hidden';
}

function findLoadedEnvelope(channelIds: Array<string>): string | null {
	for (const channelId of channelIds) {
		const channelMessages = Messages.getMessages(channelId);
		if (!channelMessages) continue;
		const envelope = channelMessages.searchFromNewest((message) => isVaultEnvelope(message.content));
		if (envelope) return envelope.content;
	}
	return null;
}

export async function unlockWithPassword(code: VaultCode, password: string): Promise<boolean> {
	if (password.length === 0) return false;
	let key: CryptoKey;
	try {
		key = await deriveVaultKey(password);
	} catch {
		return false;
	}
	if (Vault.verifier !== null) {
		try {
			const verifierText = await decryptVaultText(key, Vault.verifier);
			if (verifierText !== VAULT_VERIFIER_PLAINTEXT) return false;
		} catch {
			return false;
		}
	}
	const testChannelIds = code === VAULT_CODE_TEMP ? Vault.tempIds : Vault.hiddenIds;
	const envelope = findLoadedEnvelope(testChannelIds);
	if (envelope !== null) {
		try {
			await decryptVaultText(key, envelope);
		} catch {
			return false;
		}
	}
	if (Vault.verifier === null) {
		Vault.setVerifier(await encryptVaultText(key, VAULT_VERIFIER_PLAINTEXT, 0));
	}
	setVaultSessionKey(key);
	Vault.unlock(code);
	return true;
}

export function noteIncomingMessage(data: {channel_id: string; author: {id: string}; content?: string | null}): void {
	if (isVaultTempInvite(data.content)) {
		Vault.addTempInvite(data.channel_id);
		return;
	}
	if (isVaultHiddenInvite(data.content)) {
		Vault.addHiddenInvite(data.channel_id);
		return;
	}
	Vault.noteIncoming(data.channel_id, data.author.id);
}

/**
 * Creates a fresh private channel with the user (separate from the normal DM,
 * which the server only allows one of per pair), marks it temporary or hidden
 * locally, and sends a silent invite marker so the other client learns the
 * channel belongs in its vault. Returns the channel id.
 */
async function createVaultDm(userId: string, kind: 'temp' | 'hidden'): Promise<string> {
	const channel = await PrivateChannelCommands.createGroupDM([userId]);
	Channels.handleChannelCreate({channel});
	const channelId = channel.id;
	if (kind === 'temp') {
		if (!Vault.isTempChannel(channelId)) {
			Vault.toggleTemp(channelId);
		}
	} else {
		if (!Vault.isInHidden(channelId)) {
			Vault.toggleHidden(channelId);
		}
	}
	const nonce = SnowflakeUtils.fromTimestamp(Date.now());
	if (MessageCommands.reserveSend(channelId, nonce)) {
		void MessageCommands.send(channelId, {
			content: kind === 'temp' ? VAULT_TEMP_INVITE_CONTENT : VAULT_HIDDEN_INVITE_CONTENT,
			nonce,
			flags: MessageFlags.SUPPRESS_NOTIFICATIONS,
		});
	}
	NavigationCommands.selectChannel(ME, channelId);
	return channelId;
}

export async function createTempDm(userId: string): Promise<string> {
	return await createVaultDm(userId, 'temp');
}

export async function createHiddenDm(userId: string): Promise<string> {
	return await createVaultDm(userId, 'hidden');
}
